import { Box, Flex } from '@chakra-ui/react';
import { GetServerSideProps, NextPage } from 'next';

import { Search } from '@/components/ui/core/Search/Search';
import { ScheduleHeaderEmbedded } from '@/components/ui/features/schedule/ScheduleHeader/ScheduleHeaderEmbedded';
import { SearchQueryEmbed } from '@/components/ui/features/schedule/ScheduleQuerySearch/SearchQueryEmbed';
import { ScheduleSectionEmbed } from '@/components/ui/features/schedule/ScheduleSection/ScheduleSectionEmbed';
import { ScheduleTabsHeader } from '@/components/ui/features/schedule/SheduleTabsHeader/ScheduleTabsHeader';
import { TimeZoneInit } from '@/components/ui/features/schedule/TimeZoneInit/TimeZoneInit';
import { BugsnagError } from '@/components/ui/shared/overlays/BugsnagError/BugsnagError';
import { NoTicketCheck } from '@/components/ui/shared/overlays/NoTicketCheck/NoTicketCheck';
import { BugsnagErrorBoundary } from '@/components/ui/shared/utilities/BugsnagErrorBoundary/BugsnagErrorBoundary';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryEventAnnouncements } from '@/queries/announcements';
import { queryThinEvent, queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { generateStore } from '@/utils/generateStore';
import { Hydrate, IHydrateProps } from '@/utils/swr/Hydrate';
import { urlQueryStringValidate } from '@/utils/url-query-string';

interface IUrlQueryStringParams {
  eventId?: string;
  query?: string;
}

const EventSchedulePage: NextPage<IHydrateProps> = ({ fallback }) => {
  const { eventId, query } = useQueryParams<IUrlQueryStringParams>({
    eventId: StringParam,
    query: StringParam,
  });

  return (
    <BugsnagErrorBoundary FallbackComponent={BugsnagError}>
      <Hydrate fallback={fallback}>
        <NoTicketCheck eventId={eventId}>
          <TimeZoneInit eventId={eventId} />
          <Flex bg="white" overflow="auto" h="100%" direction="column">
            <Search minW="300px" maxW="100%" minInputLength={1} display="flex" flexDirection="column" flex={1}>
              <ScheduleHeaderEmbedded />
              {!query && <ScheduleTabsHeader />}
              {query && (
                <Box bg="gray.50">
                  <SearchQueryEmbed />
                </Box>
              )}
              {!query && <ScheduleSectionEmbed />}
            </Search>
          </Flex>
        </NoTicketCheck>
      </Hydrate>
    </BugsnagErrorBoundary>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { eventId } = urlQueryStringValidate<{ eventId: string }>(context.query, {
    eventId: StringParam,
  });

  try {
    await store.fetchResourceRelationship(queryThinEventConfigurationRelationship(eventId));
    await store.fetchResource(queryThinEvent(eventId));
    await store.fetchResources(queryEventAnnouncements(eventId));
  } catch (err) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      fallback: store.fallback,
    },
  };
};

export default EventSchedulePage;
